import {
  useForm as useReactHookUseForm,
  UseFormProps,
  FieldValues,
  FieldErrors,
  Resolver,
} from "react-hook-form";
import { useCallback } from "react";
import { validateSanitized } from "./validation";
import { useTranslation } from "./i18n";

export function useForm<TFieldValues extends FieldValues>(
  props?: UseFormProps<TFieldValues> & {
    skipDefaultValidationFor?: (keyof TFieldValues)[];
  },
) {
  const {
    context,
    criteriaMode,
    defaultValues,
    delayError,
    mode,
    reValidateMode,
    resetOptions,
    resolver,
    shouldFocusError,
    shouldUnregister,
    shouldUseNativeValidation,
    values,
    skipDefaultValidationFor = [],
  } = props || {};

  const { t } = useTranslation();

  const defaultResolver: Resolver<TFieldValues> = useCallback(
    async (fieldValues, context, options) => {
      const entries = Object.keys(fieldValues).flatMap((field) => {
        const value = fieldValues[field];
        if (
          !skipDefaultValidationFor.includes(field) &&
          typeof value === "string" &&
          !validateSanitized(value)
        ) {
          return [
            [
              field,
              {
                message: t("information_string_not_allowed"),
              },
            ],
          ];
        }
        return [];
      });

      const defaultErrors: FieldErrors<TFieldValues> =
        Object.fromEntries(entries);
      const errorsLength = Object.keys(defaultErrors).length;

      if (errorsLength || !resolver) {
        return {
          values: errorsLength === 0 ? fieldValues : {},
          errors: defaultErrors,
        };
      }

      return resolver(fieldValues, context, options);
    },
    [t, resolver, skipDefaultValidationFor],
  );

  const form = useReactHookUseForm<TFieldValues>({
    context,
    criteriaMode,
    defaultValues,
    delayError,
    mode,
    reValidateMode,
    resetOptions,
    resolver: defaultResolver,
    shouldFocusError,
    shouldUnregister,
    shouldUseNativeValidation,
    values,
  });

  return form;
}
